import {
  FieldProps,
  LocalizedString,
  RadioGroupField,
  useComponentsWrapperContext,
} from "@dbn/omnichannel-design-system-g4p";
import { Option } from "fp-ts/Option";
import { constant, constFalse, pipe } from "fp-ts/function";
import { boolean, option } from "fp-ts";
import { useFormatMessage } from "../../intl";
import { ReadOnlyYesNoField } from "../ReadOnlyYesNoField/ReadOnlyYesNoField";
import { useTooltipAccessibility } from "../Tooltip/Tooltip";

type Props = FieldProps<Option<boolean>> & {
  id?: string;
  value: Option<boolean>;
  onChange: (value: Option<boolean>) => unknown;
  description?: LocalizedString;
  cta?: {
    label: LocalizedString;
    action: () => unknown;
  };
  descriptionAriaLabel?: LocalizedString;
  tooltipCloseIconAriaLabel?: LocalizedString;
  key?: string;
};

export function YesNoRadioGroupField(props: Props) {
  const formatMessage = useFormatMessage();
  const handleOnChange = (value: option.Option<boolean>) => {
    props.onBlur();
    props.onChange(value);
  };

  const { componentType } = useComponentsWrapperContext();
  const tooltipAccessibility = useTooltipAccessibility(
    componentType,
    props.description,
    props.descriptionAriaLabel,
    props.tooltipCloseIconAriaLabel
  );

  return props.disabled ? (
    <ReadOnlyYesNoField
      size="medium"
      label={props.label}
      value={pipe(props.value, option.getOrElse(constFalse))}
      description={props.description}
      {...tooltipAccessibility}
    />
  ) : (
    <RadioGroupField
      {...tooltipAccessibility}
      {...props}
      name={`${props.name}-${Math.random()}`}
      onChange={handleOnChange}
      variant="horizontal"
      options={[true, false]}
      optionKey={value => value.toString()}
      renderOption={boolean.fold(
        () => formatMessage("No"),
        () => formatMessage("Yes")
      )}
      renderOptionChildren={constant(option.none)}
      isOptionDisabled={constFalse}
    />
  );
}
